import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
// Continents are approximated for a clean, stylized look
const LAND_DOTS: [number, number][] = [];

// Helper to add rectangular regions of dots
const addRegion = (x1: number, y1: number, x2: number, y2: number) => {
  for (let x = x1; x <= x2; x++) {
    for (let y = y1; y <= y2; y++) {
      LAND_DOTS.push([x, y]);
    }
  }
};

// North America (simplified)
const naRows: [number, number, number][] = [
  [12, 22, 6], [11, 24, 7], [10, 25, 8], [9, 25, 9], [9, 24, 10],
  [10, 24, 11], [10, 23, 12], [11, 23, 13], [12, 22, 14], [13, 21, 15],
  [14, 21, 16], [15, 21, 17], [16, 22, 18], [17, 23, 18],
];
naRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Central America
[[18, 19, 19], [19, 20, 20], [19, 21, 21]].forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// South America
const saRows: [number, number, number][] = [
  [20, 24, 22], [20, 25, 23], [20, 26, 24], [20, 26, 25],
  [21, 26, 26], [21, 26, 27], [21, 25, 28], [22, 25, 29],
  [22, 25, 30], [23, 25, 31], [23, 24, 32], [24, 24, 33],
  [24, 25, 34], [25, 25, 35],
];
saRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Europe
const euRows: [number, number, number][] = [
  [46, 50, 6], [45, 52, 7], [44, 53, 8], [44, 54, 9],
  [44, 52, 10], [44, 51, 11], [45, 50, 12], [45, 50, 13],
  [46, 49, 14], [46, 48, 15],
];
euRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Scandinavia
const scRows: [number, number, number][] = [
  [48, 50, 4], [48, 51, 5], [49, 51, 6], [49, 50, 7],
];
scRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Africa
const afRows: [number, number, number][] = [
  [46, 52, 16], [45, 53, 17], [44, 54, 18], [44, 54, 19],
  [44, 54, 20], [44, 54, 21], [45, 53, 22], [45, 53, 23],
  [46, 52, 24], [46, 52, 25], [47, 51, 26], [47, 51, 27],
  [48, 50, 28], [48, 50, 29], [49, 50, 30],
];
afRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Asia
const asRows: [number, number, number][] = [
  [55, 70, 5], [54, 72, 6], [54, 75, 7], [55, 78, 8],
  [56, 80, 9], [56, 82, 10], [55, 80, 11], [54, 78, 12],
  [54, 76, 13], [55, 74, 14], [56, 72, 15], [58, 70, 16],
  [60, 68, 17], [62, 66, 18],
];
asRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// India
const inRows: [number, number, number][] = [
  [62, 66, 16], [63, 66, 17], [63, 66, 18], [64, 66, 19],
  [64, 65, 20], [65, 65, 21],
];
inRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Southeast Asia / Indonesia
const seaRows: [number, number, number][] = [
  [72, 76, 18], [73, 78, 19], [74, 80, 20], [76, 82, 21],
  [78, 84, 22],
];
seaRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Australia
const auRows: [number, number, number][] = [
  [78, 84, 26], [77, 85, 27], [76, 86, 28], [76, 86, 29],
  [77, 85, 30], [78, 85, 31], [79, 84, 32], [80, 83, 33],
];
auRows.forEach(([x1, x2, y]) => {
  for (let x = x1; x <= x2; x++) LAND_DOTS.push([x, y]);
});

// Deduplicate dots
const dotSet = new Set(LAND_DOTS.map(([x, y]) => `${x},${y}`));
const uniqueDots = Array.from(dotSet).map(s => {
  const [x, y] = s.split(",").map(Number);
  return [x, y] as [number, number];
});

const offices = [
  {
    title: "Swedish Headquarters",
    address: "Fabriksgatan 1, 412 50 Gothenburg, Sweden",
    gx: 49, gy: 8,
    // Card position as % of container
    cardTop: "6%", cardLeft: "42%",
    labelAnchor: "middle" as const, labelDx: 0, labelDy: -16,
  },
  {
    title: "Canadian Subsidiary",
    address: "1788 West Broadway, Suite 703, Vancouver, BC, Canada V6J 1Y1",
    gx: 12, gy: 12,
    cardTop: "24%", cardLeft: "5%",
    labelAnchor: "start" as const, labelDx: 14, labelDy: -12,
  },
  {
    title: "Singapore Subsidiary",
    address: "77 Ayer Rajah Crescent #02-28, Singapore 139954",
    gx: 74, gy: 20,
    cardTop: "42%", cardLeft: "68%",
    labelAnchor: "start" as const, labelDx: 14, labelDy: 4,
  },
];

const GRID_SPACING = 10;
const DOT_RADIUS = 2;
const VIEWBOX_W = 100 * GRID_SPACING;
const VIEWBOX_H = 40 * GRID_SPACING;

const ease = [0.16, 1, 0.3, 1] as const;

const OfficeMap = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease }}
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: "2.5 / 1", minHeight: 320 }}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        className="w-full h-full absolute inset-0"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background dots — faint grid */}
        {Array.from({ length: 100 }, (_, x) =>
          Array.from({ length: 40 }, (_, y) => {
            const isLand = dotSet.has(`${x},${y}`);
            if (!isLand && (x % 4 !== 0 || y % 4 !== 0)) return null;
            return (
              <circle
                key={`bg-${x}-${y}`}
                cx={x * GRID_SPACING + GRID_SPACING / 2}
                cy={y * GRID_SPACING + GRID_SPACING / 2}
                r={isLand ? 0 : 0.8}
                fill="hsl(var(--muted-foreground))"
                opacity={0.08}
              />
            );
          })
        )}

        {/* Land dots */}
        {uniqueDots.map(([x, y]) => (
          <circle
            key={`land-${x}-${y}`}
            cx={x * GRID_SPACING + GRID_SPACING / 2}
            cy={y * GRID_SPACING + GRID_SPACING / 2}
            r={DOT_RADIUS}
            fill="hsl(var(--muted-foreground))"
            opacity={0.18}
          />
        ))}

        {/* Connection arcs */}
        <path
          d={`M ${12 * GRID_SPACING + 5} ${12 * GRID_SPACING + 5} Q ${30 * GRID_SPACING} ${2 * GRID_SPACING} ${49 * GRID_SPACING + 5} ${8 * GRID_SPACING + 5}`}
          stroke="hsl(var(--primary))"
          strokeWidth="1"
          fill="none"
          opacity="0.2"
          strokeDasharray="4 3"
        />
        <path
          d={`M ${49 * GRID_SPACING + 5} ${8 * GRID_SPACING + 5} Q ${62 * GRID_SPACING} ${4 * GRID_SPACING} ${74 * GRID_SPACING + 5} ${20 * GRID_SPACING + 5}`}
          stroke="hsl(var(--primary))"
          strokeWidth="1"
          fill="none"
          opacity="0.2"
          strokeDasharray="4 3"
        />

        {/* Small dot markers at office locations */}
        {offices.map((office) => {
          const cx = office.gx * GRID_SPACING + GRID_SPACING / 2;
          const cy = office.gy * GRID_SPACING + GRID_SPACING / 2;
          return (
            <circle key={office.title} cx={cx} cy={cy} r="4" fill="hsl(var(--primary))" />
          );
        })}
      </svg>

      {/* Overlay office cards */}
      {offices.map((office, i) => (
        <motion.div
          key={office.title}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 + i * 0.1, ease }}
          className="absolute hidden md:block"
          style={{ top: office.cardTop, left: office.cardLeft }}
        >
          <div className="bg-card/90 backdrop-blur-sm rounded-lg px-4 py-3 border border-border shadow-sm max-w-[200px]">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="text-primary shrink-0" size={14} />
              <h3 className="font-display text-xs font-semibold text-foreground truncate">{office.title}</h3>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">{office.address}</p>
          </div>
        </motion.div>
      ))}

      {/* Mobile: cards below map */}
      <div className="md:hidden relative px-4 pb-4 pt-2 grid gap-3">
        {offices.map((office) => (
          <div key={office.title} className="bg-card/90 backdrop-blur-sm rounded-lg px-4 py-3 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="text-primary shrink-0" size={14} />
              <h3 className="font-display text-xs font-semibold text-foreground">{office.title}</h3>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">{office.address}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default OfficeMap;
